/* ---------------------------------------------------------------------------
   The session's one and only AudioContext.

   iOS Safari will only ever let an AudioContext leave the "suspended" state
   inside a real user gesture, and a context that has been unlocked stays
   unlocked for the life of the page. Building a second context mid-round is the
   classic way to end up with a silent playback phase on an iPhone — so this
   module owns exactly one, forever, and everything else borrows it.
   --------------------------------------------------------------------------- */

type AudioContextCtor = new (options?: AudioContextOptions) => AudioContext;

interface AudioGlobals {
  AudioContext?: AudioContextCtor;
  webkitAudioContext?: AudioContextCtor;
}

export type AudioLifecycleState = AudioContextState | "unavailable" | "uninitialized";

let context: AudioContext | null = null;
let unlocked = false;
const stateListeners = new Set<(state: AudioLifecycleState) => void>();

function ctor(): AudioContextCtor | null {
  if (typeof window === "undefined") return null;
  const globals = window as unknown as AudioGlobals;
  return globals.AudioContext ?? globals.webkitAudioContext ?? null;
}

export function isAudioSupported(): boolean {
  return ctor() !== null;
}

function emitState(): void {
  const state = audioState();
  for (const listener of stateListeners) listener(state);
}

/**
 * The shared context, created on first call.
 * Throws when Web Audio is missing entirely — check `isAudioSupported()` first
 * if you want to degrade rather than crash.
 */
export function getAudioContext(): AudioContext {
  if (context) return context;

  const Ctor = ctor();
  if (!Ctor) {
    throw new Error("This browser has no Web Audio support, so dubs cannot be played back.");
  }

  context = new Ctor({ latencyHint: "interactive" });
  context.addEventListener?.("statechange", emitState);
  unlocked = context.state === "running";
  return context;
}

/** The context if one has already been built. Never constructs one. */
export function peekAudioContext(): AudioContext | null {
  return context;
}

export function audioState(): AudioLifecycleState {
  if (!isAudioSupported()) return "unavailable";
  if (!context) return "uninitialized";
  return context.state;
}

export function isAudioUnlocked(): boolean {
  return unlocked && context?.state === "running";
}

/** Subscribe to context state changes. Returns an unsubscribe function. */
export function onAudioStateChange(listener: (state: AudioLifecycleState) => void): () => void {
  stateListeners.add(listener);
  return () => {
    stateListeners.delete(listener);
  };
}

/**
 * Call this from inside a real user gesture (pointerdown/click/touchend) before
 * the game needs sound — the lobby "I'm ready" button is the natural place.
 *
 * Resumes the context and pushes one silent buffer through it, which is what
 * actually flips iOS out of its muted state. Safe to call repeatedly.
 */
export async function unlockAudio(): Promise<boolean> {
  if (!isAudioSupported()) return false;

  const ctx = getAudioContext();
  try {
    if (ctx.state !== "running") await ctx.resume();
  } catch {
    /* Fall through: the silent-buffer nudge below sometimes succeeds anyway. */
  }

  try {
    const buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);
    source.onended = () => source.disconnect();
  } catch {
    /* Non-fatal — some browsers reject zero-length sources. */
  }

  unlocked = ctx.state === "running";
  emitState();
  return unlocked;
}

/**
 * Nudge the context back to "running" after an interruption (incoming call,
 * tab backgrounded, iOS route change). Does not create a context.
 */
export async function resumeIfSuspended(): Promise<AudioLifecycleState> {
  if (!context) return audioState();
  if (context.state === "suspended") {
    try {
      await context.resume();
    } catch {
      /* Still suspended — the caller decides how loud to complain. */
    }
  }
  unlocked = context.state === "running";
  return audioState();
}

/** Suspend the shared context to save battery between rounds. Never closes it. */
export async function suspendAudio(): Promise<void> {
  if (context && context.state === "running") {
    try {
      await context.suspend();
    } catch {
      /* Ignore — suspension is an optimisation, not a requirement. */
    }
  }
}

/**
 * Decode compressed audio (a recorded sub-chunk) into an AudioBuffer.
 * Copies the input because several engines detach the source ArrayBuffer, and
 * handles Safari's legacy callback-only signature.
 */
export function decodeAudio(data: ArrayBuffer): Promise<AudioBuffer> {
  const ctx = getAudioContext();
  const copy = data.slice(0);

  return new Promise<AudioBuffer>((resolve, reject) => {
    const onError = (err: unknown) =>
      reject(new Error("Could not decode this recording — the audio may be corrupt.", { cause: err }));

    let settled = false;
    const done = (buffer: AudioBuffer) => {
      if (settled) return;
      settled = true;
      resolve(buffer);
    };
    const failed = (err: unknown) => {
      if (settled) return;
      settled = true;
      onError(err);
    };

    try {
      const maybePromise = ctx.decodeAudioData(copy, done, failed);
      if (maybePromise && typeof maybePromise.then === "function") {
        maybePromise.then(done, failed);
      }
    } catch (err) {
      failed(err);
    }
  });
}

export async function decodeBlob(blob: Blob): Promise<AudioBuffer> {
  return decodeAudio(await blob.arrayBuffer());
}

/** True when the buffer contains anything other than digital silence. */
export function isAudibleBuffer(buffer: AudioBuffer, threshold = 1e-4): boolean {
  const stride = Math.max(1, Math.floor(buffer.sampleRate / 4_000));
  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const samples = buffer.getChannelData(channel);
    for (let i = 0; i < samples.length; i += stride) {
      if (Math.abs(samples[i]) > threshold) return true;
    }
  }
  return false;
}

/**
 * Tear the context down. Only for page teardown or tests — calling this
 * mid-game means the next `getAudioContext()` builds a fresh, locked context,
 * which is exactly the iOS failure mode this module exists to prevent.
 */
export async function closeAudioContext(): Promise<void> {
  const ctx = context;
  context = null;
  unlocked = false;
  if (ctx) {
    ctx.removeEventListener?.("statechange", emitState);
    try {
      await ctx.close();
    } catch {
      /* Already closed. */
    }
  }
  emitState();
}
